import React, { useState } from 'react';

import { Package } from 'lucide-react';
import { useQuery } from 'react-query';
import { DotBricksLoading } from 'src/common/components';

import { fetchHubFeatures, HubFeatureStatus } from '../api';
import {
  CATEGORY_COLOR,
  CATEGORY_OPTIONS,
  FEATURE_CATALOG_QUERY_KEY,
  FEATURE_STATUS_LABEL,
  NO_CATEGORY_LABEL,
} from '../constants';
import { FeatureCatalogEntry } from '../types';
import {
  CatalogSortOrder,
  CategoryFilterValue,
  groupCatalogEntries,
  matchesCatalogFilters,
  NO_CATEGORY_FILTER,
} from '../utils';

import FeatureDetail, {
  categoryChipStyle,
  entryCategoryColor,
  entryCategoryLabel,
  FeatureBadges,
} from './FeatureDetail';

type ViewMode = 'list' | 'timeline';

const SORT_OPTIONS: { value: CatalogSortOrder; label: string }[] = [
  { value: 'newest', label: 'Más reciente primero' },
  { value: 'oldest', label: 'Más antiguo primero' },
];
// Discontinued features are never offered as a filter in Orbit.
const STATUS_FILTERS: HubFeatureStatus[] = ['active', 'legacy'];

const CATEGORY_FILTERS: { value: CategoryFilterValue; label: string }[] = [
  ...CATEGORY_OPTIONS,
  { value: NO_CATEGORY_FILTER, label: NO_CATEGORY_LABEL },
];

const CATEGORY_FILTER_COLOR = (value: CategoryFilterValue): string =>
  value === NO_CATEGORY_FILTER ? '#6B7280' : CATEGORY_COLOR[value];

const truncatedText = (lines: number): React.CSSProperties => ({
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
});

const CatalogTab: React.FC = () => {
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<ViewMode>('list');
  const [prevMode, setPrevMode] = useState<ViewMode>('list');
  const [selectedCats, setSelectedCats] = useState<CategoryFilterValue[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [sortOrder, setSortOrder] = useState<CatalogSortOrder>('newest');
  const [statusFilter, setStatusFilter] = useState<HubFeatureStatus>('active');

  const { data, isLoading } = useQuery([FEATURE_CATALOG_QUERY_KEY, 'hub', statusFilter], () =>
    fetchHubFeatures(statusFilter)
  );
  const entries = data?.data.results ?? [];

  const allTags = Array.from(new Set(entries.flatMap((e) => e.tags))).sort();

  const filtered = entries.filter((e) =>
    matchesCatalogFilters(e, query, selectedCats, selectedTags)
  );
  const groups = groupCatalogEntries(filtered, sortOrder);

  const toggleCat = (cat: CategoryFilterValue): void => {
    setSelectedCats((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const toggleTag = (tag: string): void => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const setMode2 = (m: ViewMode): void => {
    setPrevMode(mode);
    setMode(m);
  };

  const selectedEntry = entries.find((e) => e.id === selectedId);
  if (selectedEntry) {
    return (
      <FeatureDetail
        key={selectedEntry.id}
        entry={selectedEntry}
        related={entries
          .filter((e) => e.category === selectedEntry.category && e.id !== selectedEntry.id)
          .slice(0, 3)}
        onBack={() => setSelectedId(null)}
        onSelect={setSelectedId}
      />
    );
  }

  if (isLoading) return <DotBricksLoading />;

  const chipBase: React.CSSProperties = {
    borderRadius: 999,
    padding: '7px 12px',
    fontSize: 12.5,
    fontWeight: 600,
    lineHeight: 1,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    border: '1px solid',
    transition: 'background 180ms ease, border-color 180ms ease, color 180ms ease',
  };

  const listAnim = prevMode === 'timeline' ? 'dgInLeft' : 'dgFade';
  const timelineAnim = 'dgInRight';

  const renderEntryChips = (entry: FeatureCatalogEntry, tagLimit: number): React.ReactNode => (
    <>
      <span style={{ ...categoryChipStyle(entryCategoryColor(entry)), padding: '3px 9px' }}>
        {entryCategoryLabel(entry)}
      </span>
      {entry.tags.slice(0, tagLimit).map((tag) => (
        <span
          key={tag}
          style={{ ...categoryChipStyle('#6B7280'), fontSize: 11, padding: '3px 8px' }}
        >
          {tag}
        </span>
      ))}
      {entry.tags.length > tagLimit && (
        <span style={{ fontSize: 11, color: '#9CA3AF' }}>+{entry.tags.length - tagLimit}</span>
      )}
      <FeatureBadges entry={entry} />
    </>
  );

  return (
    <div className="flex flex-col h-full gap-4 p-6 overflow-y-auto">
      {/* Search + segmented control */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <div style={{ position: 'relative', flex: '1 1 280px', minWidth: 200 }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#858C99"
            strokeWidth="2"
            style={{ position: 'absolute', left: 13, top: 12 }}
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
          <input
            style={{
              width: '100%',
              height: 40,
              padding: '0 12px 0 36px',
              border: '1px solid #D8DADF',
              borderRadius: 999,
              fontSize: 14,
              color: '#111A2B',
              background: '#fff',
              outline: 'none',
            }}
            placeholder="Buscar una funcionalidad…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <select
          aria-label="Ordenar lanzamientos"
          className="flex-shrink-0 h-10 px-3 text-sm text-gray-900 bg-white border border-gray-300 rounded-full cursor-pointer"
          value={sortOrder}
          onChange={(e) =>
            setSortOrder(
              SORT_OPTIONS.find(({ value }) => value === e.target.value)?.value ?? 'newest'
            )
          }
        >
          {SORT_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        {/* Segmented control */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            padding: 3,
            background: '#DDE3EE',
            borderRadius: 999,
            flexShrink: 0,
            boxShadow: 'inset 0 1px 2px rgba(17,26,43,.08)',
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 3,
              left: 3,
              height: 32,
              width: 104,
              borderRadius: 999,
              background: '#fff',
              boxShadow: '0 2px 6px rgba(17,26,43,.16)',
              transition: 'transform 380ms cubic-bezier(.34,1.3,.4,1)',
              transform: mode === 'list' ? 'translateX(0)' : 'translateX(104px)',
            }}
          />
          {(['list', 'timeline'] as ViewMode[]).map((m) => (
            <button
              key={m}
              type="button"
              style={{
                position: 'relative',
                zIndex: 1,
                border: 0,
                background: 'none',
                borderRadius: 999,
                height: 32,
                width: 104,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'color 240ms ease',
                color: mode === m ? '#2E4774' : '#6B7280',
              }}
              onClick={() => setMode2(m)}
            >
              {m === 'list' ? 'Lista' : 'Timeline'}
            </button>
          ))}
        </div>
      </div>

      {/* Status filter: Active by default, Legacy on demand */}
      <div
        role="group"
        aria-label="Estado"
        style={{ display: 'flex', gap: 7, alignItems: 'center' }}
      >
        {STATUS_FILTERS.map((status) => (
          <button
            key={status}
            type="button"
            aria-pressed={statusFilter === status}
            style={{
              ...chipBase,
              ...(statusFilter === status
                ? { background: '#2E4774', borderColor: '#2E4774', color: '#fff' }
                : { background: '#fff', borderColor: '#D8DADF', color: '#545964' }),
            }}
            onClick={() => {
              setStatusFilter(status);
              setSelectedCats([]);
              setSelectedTags([]);
              setSelectedId(null);
            }}
          >
            {status === 'active' ? 'Activos' : FEATURE_STATUS_LABEL[status]}
          </button>
        ))}
      </div>

      {/* Category chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center' }}>
        <button
          type="button"
          style={{
            ...chipBase,
            ...(selectedCats.length === 0 && selectedTags.length === 0
              ? { background: '#2E4774', borderColor: '#2E4774', color: '#fff' }
              : { background: '#fff', borderColor: '#D8DADF', color: '#545964' }),
          }}
          onClick={() => {
            setSelectedCats([]);
            setSelectedTags([]);
          }}
        >
          Todas
        </button>
        {CATEGORY_FILTERS.map(({ value, label }) => {
          const isActive = selectedCats.includes(value);
          const cc = CATEGORY_FILTER_COLOR(value);
          return (
            <button
              key={value}
              type="button"
              style={{
                ...chipBase,
                ...(isActive
                  ? { background: `${cc}1a`, borderColor: `${cc}60`, color: cc }
                  : { background: '#fff', borderColor: '#D8DADF', color: '#545964' }),
              }}
              onClick={() => toggleCat(value)}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Tag filter chips (only shown when any feature has tags) */}
      {allTags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center' }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: '#9CA3AF',
              marginRight: 2,
            }}
          >
            Etiquetas:
          </span>
          {allTags.map((tag) => {
            const isActive = selectedTags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                style={{
                  ...chipBase,
                  ...(isActive
                    ? { background: '#6B72801a', borderColor: '#6B728060', color: '#374151' }
                    : { background: '#fff', borderColor: '#D8DADF', color: '#545964' }),
                }}
                onClick={() => toggleTag(tag)}
              >
                {tag}
              </button>
            );
          })}
        </div>
      )}

      {/* Heading + count */}
      {filtered.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
          <h2
            style={{
              font: "700 18px/1.3 'Montserrat',sans-serif",
              color: '#2E4774',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              margin: 0,
            }}
          >
            {selectedCats.length === 1
              ? CATEGORY_FILTERS.find((f) => f.value === selectedCats[0])?.label
              : selectedCats.length > 1
              ? 'Selección'
              : 'Todas las funcionalidades'}
          </h2>
          <span style={{ fontSize: 13, color: '#6B7280' }}>
            {filtered.length} funcionalidades lanzadas
          </span>
        </div>
      )}

      {/* Empty state */}
      {filtered.length === 0 && (
        <div
          style={{
            padding: '56px 20px',
            textAlign: 'center',
            background: '#fff',
            border: '1px solid #D8DADF',
            borderRadius: 14,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <Package size={32} color="#D8DADF" />
          <p style={{ fontSize: 14, color: '#6B7280', margin: 0 }}>
            {entries.length === 0
              ? statusFilter === 'legacy'
                ? 'No hay features Legacy.'
                : 'Todavía no hay features lanzados.'
              : 'Ninguna funcionalidad coincide con el filtro.'}
          </p>
        </div>
      )}

      {/* LIST VIEW */}
      {mode === 'list' && filtered.length > 0 && (
        <div
          className="space-y-6"
          style={{ animation: `${listAnim} 320ms cubic-bezier(.2,.7,.3,1) both` }}
        >
          {groups.map((group) => (
            <section key={group.key} aria-label={group.label}>
              <h3
                style={{
                  font: "700 12px/1.2 'Montserrat',sans-serif",
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: '#6B7280',
                  marginBottom: 12,
                }}
              >
                {group.label}
              </h3>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))',
                  gap: 16,
                  alignItems: 'stretch',
                }}
              >
                {group.entries.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    className="dg-feature-card text-left transform hover:-translate-y-0.5 transition-all"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      padding: '18px 20px',
                      borderRadius: 14,
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedId(entry.id)}
                  >
                    <div
                      style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}
                    >
                      {renderEntryChips(entry, 2)}
                    </div>
                    <div
                      style={{
                        fontSize: 17,
                        fontWeight: 600,
                        lineHeight: 1.32,
                        color: '#111A2B',
                        letterSpacing: '-.01em',
                        marginTop: 6,
                      }}
                    >
                      {entry.title}
                    </div>
                    {entry.customer_benefit && (
                      <p
                        style={{
                          fontSize: 14,
                          lineHeight: 1.55,
                          color: '#545964',
                          margin: 0,
                          ...truncatedText(3),
                        }}
                      >
                        {entry.customer_benefit}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* TIMELINE VIEW */}
      {mode === 'timeline' && filtered.length > 0 && (
        <div
          style={{ animation: `${timelineAnim} 320ms cubic-bezier(.2,.7,.3,1) both` }}
          className="space-y-6"
        >
          {groups.map((group) => (
            <div
              key={group.key}
              style={{
                display: 'grid',
                gridTemplateColumns: '160px minmax(0,1fr)',
                gap: 20,
                alignItems: 'start',
              }}
            >
              <div
                style={{
                  paddingTop: 20,
                  textAlign: 'right',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: '#6B7280',
                  position: 'sticky',
                  top: 16,
                }}
              >
                {group.label}
              </div>
              <div
                style={{
                  borderLeft: '2px solid #C9D8EC',
                  padding: '0 0 8px 24px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                {group.entries.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    className="dg-feature-card text-left transform hover:-translate-y-0.5 transition-all"
                    style={{
                      position: 'relative',
                      padding: '16px 20px',
                      borderRadius: 14,
                      cursor: 'pointer',
                      marginTop: 12,
                    }}
                    onClick={() => setSelectedId(entry.id)}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        left: -31,
                        top: 24,
                        width: 12,
                        height: 12,
                        borderRadius: 999,
                        background: '#2E4774',
                        border: '2px solid #fff',
                        boxShadow: '0 0 0 2px #C9D8EC',
                      }}
                    />
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        flexWrap: 'wrap',
                        marginBottom: 8,
                      }}
                    >
                      {renderEntryChips(entry, 1)}
                    </div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        lineHeight: 1.35,
                        color: '#111A2B',
                        marginBottom: 6,
                      }}
                    >
                      {entry.title}
                    </div>
                    {entry.customer_benefit && (
                      <p
                        style={{
                          fontSize: 13.5,
                          lineHeight: 1.55,
                          color: '#545964',
                          margin: 0,
                          ...truncatedText(2),
                        }}
                      >
                        {entry.customer_benefit}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CatalogTab;
